<template>
  <v-card tile>
    <v-btn color="primary" dark class="ma-2" @click="createCourse()" v-if="isAdmin">Ouvrir un nouveau cours</v-btn>
    <v-dialog v-model="dialog" width="80%">
      <course-form
        :course="course"
        :formTitle="'Ouvrir un nouveau cours'"
        @save="handleSaveCourse"
        @cancel="dialog = false"
      />
    </v-dialog>
    <v-card-title>
      Liste de cours
      <v-switch v-model="isShowAllCourses" :label="`Tout`" class="mx-5"></v-switch>
      <v-spacer></v-spacer>
      <v-text-field v-model="search" append-icon="mdi-magnify" label="Chercher" single-line hide-details></v-text-field>
    </v-card-title>
    <v-data-table
      :headers="headers"
      :items="filteredCourses"
      :loading="loading"
      :no-data-text="'Aucun resultat trouvé'"
      :search="search"
      sort-by="id"
      sort-desc
      :items-per-page="100"
      :footer-props="{
        itemsPerPageText: 'Nombre de lignes',
        pageText: 'Lignes {0}-{1} de {2}',
      }"
      class="course-table"
    >
      <template v-slot:body="{ items }">
        <tbody>
          <tr v-for="item in items" :key="item.id" @click="navigateToCourse(item)" class="clickable course-row">
            <!-- Type -->
            <td>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    small
                    :color="getTypeColor(item.type)"
                    text-color="white"
                    label
                    v-bind="attrs"
                    v-on="on"
                  >
                    <template v-if="item.type === 'frdistance'">
                      FR
                      <v-icon small right>mdi-webcam</v-icon>
                    </template>
                    <template v-else>
                      {{ item.type ? item.type.toUpperCase() : '' }}
                    </template>
                  </v-chip>
                </template>
                <span>{{ item.type_full }}</span>
              </v-tooltip>
            </td>
            <!-- Nom -->
            <td class="font-weight-medium">{{ item.name }}</td>
            <!-- Niveau -->
            <td>
              <v-chip
                v-for="level in parseLevels(item.levelsString)"
                :key="level"
                x-small
                :color="getLevelColor(level)"
                :text-color="getLevelTextColor(level)"
                class="mr-1 mb-1"
              >
                {{ level }}
              </v-chip>
            </td>
            <!-- Date de début -->
            <td>
              <div class="d-flex align-center">
                <v-icon small color="green darken-1" class="mr-1">mdi-calendar-start</v-icon>
                <span class="text-no-wrap">{{ item.start | dateToHuman }}</span>
              </div>
            </td>
            <!-- Date de fin -->
            <td>
              <div class="d-flex align-center">
                <v-icon small color="red darken-1" class="mr-1">mdi-calendar-end</v-icon>
                <span class="text-no-wrap">{{ item.end | dateToHuman }}</span>
              </div>
            </td>
            <!-- Horaires -->
            <td>
              <div v-if="item.agenda" class="d-flex align-center">
                <v-icon small color="blue-grey" class="mr-1">mdi-clock-outline</v-icon>
                <span class="text-caption">{{ item.agenda }}</span>
              </div>
              <span v-else class="text-caption grey--text">-</span>
            </td>
            <!-- Jours -->
            <td>
              <div class="d-flex align-center">
                <span
                  v-for="day in weekDays"
                  :key="day.key"
                  class="day-circle mr-1"
                  :class="{ 'day-active': isDayActive(item.courseDays, day.key) }"
                  :title="day.full"
                >
                  {{ day.abbr }}
                </span>
              </div>
            </td>
            <!-- Salle -->
            <td class="text-no-wrap">
              <div v-if="item.room" class="d-flex align-center">
                <v-icon small color="orange darken-2" class="mr-1">mdi-door</v-icon>
                <span>{{ item.room }}</span>
              </div>
              <span v-else class="text-caption grey--text">-</span>
            </td>
            <!-- Formateur/trice -->
            <td>
              <template v-if="item.teachers">
                <v-chip
                  v-for="teacher in parseTeachers(item.teachers)"
                  :key="teacher"
                  small
                  :color="getTeacherColor(teacher)"
                  text-color="white"
                  class="mr-1 mb-1"
                >
                  {{ teacher }}
                </v-chip>
              </template>
              <span v-else class="grey--text">-</span>
            </td>
            <!-- Lien -->
            <td>
              <v-tooltip bottom v-if="item.drive_link">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :href="item.drive_link"
                    target="_blank"
                    icon
                    small
                    color="blue"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop
                  >
                    <v-icon small>mdi-google-drive</v-icon>
                  </v-btn>
                </template>
                <span>Ouvrir le dossier Drive</span>
              </v-tooltip>
              <v-icon v-else small color="grey lighten-1">mdi-link-off</v-icon>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
  </v-card>
</template>
<script>
import Course from "@/models/Course"
import CourseForm from "@/components/courses/CourseForm.vue"
import { mapGetters } from "vuex"
import { getCourseTypeColor, getHashColor } from "@/utils/helpers"

export default {
  components: {
    CourseForm,
  },
  async mounted() {
    await this.getDataFromApi()
  },
  data() {
    return {
      menu1: false,
      menu2: false,
      menu3: false,
      menu4: false,
      valid: false,
      search: "",
      headers: [
        { text: "Type", value: "full_type" },
        { text: "Nom", value: "name" },
        { text: "Niveau", value: "levelsString" },
        { text: "Début", value: "startString" },
        { text: "Fin", value: "endString" },
        { text: "Horaires", value: "start_hours" },
        { text: "Jours", value: "courseDays" },
        { text: "Salle", value: "room", width: "180px" },
        { text: "Formateur/trice", value: "teachers" },
        { text: "Lien", value: "drive_link" },
      ],
      loading: true,
      dialog: false,
      course: new Course(),
      isShowAllCourses: false,
      weekDays: [
        { key: 'lundi', abbr: 'L', full: 'Lundi' },
        { key: 'mardi', abbr: 'M', full: 'Mardi' },
        { key: 'mercredi', abbr: 'M', full: 'Mercredi' },
        { key: 'jeudi', abbr: 'J', full: 'Jeudi' },
        { key: 'vendredi', abbr: 'V', full: 'Vendredi' },
      ],
    }
  },
  computed: {
    ...mapGetters("Auth", ["isAdmin"]),
    courses() {
      return Course.query().all()
    },
    filteredCourses() {
      if (this.isShowAllCourses) {
        return this.courses
      }

      let filtered = []

      let beforeWeek = new Date()
      beforeWeek.setDate(beforeWeek.getDate() - 7)

      this.courses.forEach((c) => {
        if (Date.parse(c.end) >= beforeWeek) {
          filtered.push(c)
        }
      })
      return filtered
    },
  },
  methods: {
    async handleSaveCourse(course) {
      this.dialog = false
      this.loading = true
      if (course.id != null) {
        await Course.update(course)
      } else {
        await Course.create(course)
      }
      await this.getDataFromApi()
    },
    async getDataFromApi() {
      await Course.get().finally(() => {
        this.loading = false
      })
    },
    createCourse() {
      this.course = new Course()
      this.course.type = "fr"
      this.dialog = true
    },
    navigateToCourse(course) {
      this.$router.push(`/courses/${course.id}`)
    },
    parseLevels(levelsString) {
      if (!levelsString) return []
      return levelsString.split(',').map(l => l.trim()).filter(l => l)
    },
    isDayActive(courseDays, dayKey) {
      if (!courseDays) return false
      return courseDays.toLowerCase().includes(dayKey)
    },
    parseTeachers(teachersString) {
      if (!teachersString) return []
      return teachersString.split(',').map(t => t.trim()).filter(t => t)
    },
    getLevelColor(level) {
      const levelLower = level.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      // Gradient from light green (A0/A1/Debutant) to dark red (C2/Avance)
      if (levelLower.includes('a0')) return '#C8E6C9' // very light green
      if (levelLower.includes('alpha')) return '#C8E6C9' // very light green (Alphabetisation)
      if (levelLower.includes('a1')) return '#A5D6A7' // light green
      if (levelLower.includes('a2')) return '#AED581' // lime
      if (levelLower.includes('b1')) return '#FFD54F' // amber
      if (levelLower.includes('b2')) return '#FFB74D' // orange
      if (levelLower.includes('c1')) return '#E57373' // red lighten-2
      if (levelLower.includes('c2')) return '#C62828' // dark red
      if (levelLower.includes('deb')) return '#C8E6C9' // very light green (Debutant)
      if (levelLower.includes('interm')) return '#FFD54F' // amber (Intermediaire)
      if (levelLower.includes('avanc')) return '#E57373' // red (Avance)
      return 'grey'
    },
    getLevelTextColor(level) {
      const levelLower = level.toLowerCase()
      // Dark text for light backgrounds, white for dark
      if (levelLower.includes('c2')) return 'white'
      return '#333'
    },
    getTypeColor: getCourseTypeColor,
    getTeacherColor: getHashColor,
  },
}
</script>

<style scoped>
.course-table .course-row {
  transition: background-color 0.15s ease;
}

.course-table .course-row:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.course-table td {
  vertical-align: middle !important;
}

.day-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  background-color: #EEEEEE;
  color: #BDBDBD;
  cursor: default;
}

.day-circle.day-active {
  background-color: #1976D2;
  color: white;
}
</style>
